import React, { useState, useEffect, useCallback } from 'react';
import aceLogo from '../assets/Ace logo.png'; // Add this import (ensure the logo is placed in frontend/src/assets/Ace logo.png)

const MockupListPage = ({ 
    logoFile, 
    onMockupSelect, 
    selectedMockupId, 
    onGenerateZachsMockups,
    isGeneratingZachs,
    zachsBatchButtonStyle,
    searchQuery,
    isSearching,
    generatedMockups = {},
    isGeneratingMockups = false,
    onGenerateMockups,
    mockupLoading = {},
    onSearchChange,
    onSearch
}) => {
    const [mockups, setMockups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalMockups, setTotalMockups] = useState(0);

    // State for search functionality
    const [activeSearchTerm, setActiveSearchTerm] = useState(''); // Default to no active search term

    const fetchData = useCallback(async (pageToFetch, termToSearch, searchMode) => {
        setIsLoading(true);
        setError(null);
        
        const API_URL = process.env.REACT_APP_API_URL || '';
        let url = '';
        let params = new URLSearchParams({ page: pageToFetch.toString() });

        if (searchMode && termToSearch) {
            params.append('q', termToSearch);
            url = `${API_URL}/api/mockups/search?${params.toString()}`;
            console.log(`[MockupListPage] Searching for: "${termToSearch}", page: ${pageToFetch}`);
        } else {
            url = `${API_URL}/api/mockups?${params.toString()}`;
            console.log(`[MockupListPage] Fetching all mockups, page: ${pageToFetch}`);
        }

        try {
            const response = await fetch(url);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Server error, no valid JSON.'}));
                throw new Error(errorData.message || `Server error: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                setMockups(data.mockups || []);
                setCurrentPage(parseInt(data.page, 10));
                setTotalPages(data.pages || 0);
                setTotalMockups(data.total || 0);
            } else {
                throw new Error(data.message || 'API indicated failure.');
            }
        } catch (err) {
            setError(err.message || 'Unexpected error.');
            setMockups([]);
        } finally {
            setIsLoading(false);
        }
    }, []); // useCallback will memoize this function

    // Initial fetch: List All mockups on page 1
    useEffect(() => {
        // Fetch based on searchQuery and isSearching props
        if (isSearching && searchQuery) {
            fetchData(1, searchQuery, true);
            setActiveSearchTerm(searchQuery);
        } else {
            fetchData(1, null, false);
            setActiveSearchTerm('');
        }
    }, [fetchData, searchQuery, isSearching]);

    // Add effect to update the parent button's onClick
    useEffect(() => {
        const btn = document.getElementById('generate-mockups-btn');
        if (btn && onGenerateMockups) {
            btn.onclick = () => onGenerateMockups(mockups);
        }
    }, [mockups, onGenerateMockups]);

    // Update color styles for red, white, and blue branding
    const aceRed = '#d7262c';
    const aceBlue = '#003366';
    const aceWhite = '#ffffff';

    const baseMockupItemStyle = {
        border: `2px solid ${aceBlue}`,
        marginBottom: '15px',
        padding: '15px',
        width: '390px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        backgroundColor: aceWhite,
        transition: 'border-color 0.3s ease, transform 0.2s ease'
    };
    const selectedMockupItemStyle = {
        ...baseMockupItemStyle,
        borderColor: aceRed,
        transform: 'scale(1.03)',
        boxShadow: `0 0 10px ${aceRed}80`
    };
    const clickableMockupItemStyle = {
        ...baseMockupItemStyle,
        cursor: 'pointer',
        borderColor: aceBlue
    };
    const mockupImageStyle = {
        objectFit: 'contain',
        marginBottom: '15px',
        width: '100%',
        maxWidth: '360px',
        height: 'auto',
        maxHeight: '360px',
    };

    const mockupGridStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        marginTop: '20px',
        justifyContent: 'center'
    };

    const controlsContainerStyle = {
        marginBottom: '20px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        flexWrap: 'wrap',
        background: `linear-gradient(90deg, ${aceRed} 0%, ${aceWhite} 50%, ${aceBlue} 100%)`,
        padding: '10px',
        borderRadius: '8px'
    };
    const searchInputStyle = {
        padding: '8px',
        border: `1.5px solid ${aceBlue}`,
        borderRadius: '4px',
        flex: '1 1 200px',
        background: aceWhite,
        color: aceBlue
    };
    const buttonStyle = {
        padding: '8px 15px',
        cursor: 'pointer',
        background: aceRed,
        color: aceWhite,
        border: `1.5px solid ${aceBlue}`,
        borderRadius: '4px',
        fontWeight: 'bold',
        transition: 'background 0.2s, color 0.2s',
    };

    const paginationButtonStyle = {
        padding: '10px 28px',
        margin: '0 8px',
        borderRadius: '999px',
        background: `linear-gradient(90deg, ${aceBlue} 0%, ${aceRed} 100%)`,
        color: aceWhite,
        border: 'none',
        fontWeight: 'bold',
        fontSize: '1.1em',
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        cursor: 'pointer',
        transition: 'background 0.2s, transform 0.2s',
        outline: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    };
    const paginationButtonHoverStyle = {
        background: `linear-gradient(90deg, ${aceRed} 0%, ${aceBlue} 100%)`,
        transform: 'scale(1.05)',
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) fetchData(currentPage - 1, activeSearchTerm, isSearching);
    };
    const handleNextPage = () => {
        if (currentPage < totalPages) fetchData(currentPage + 1, activeSearchTerm, isSearching);
    };

    return (
        <div style={{ padding: '20px', margin: '20px', border: `2px solid ${aceBlue}`, background: aceWhite, borderRadius: '12px' }}>
            {/* Removed ACE Specialties Logo Header and search bar */}
            
            {error && <p style={{ color: 'red', marginTop: '10px' }}>Error: {error}</p>}
            
            {isLoading && mockups.length === 0 && <p style={{ textAlign: 'center' }}>Loading mockups...</p>} 

            {isLoading && mockups.length > 0 && <p style={{ textAlign: 'center' }}>Loading more mockups...</p>}

            {!isLoading && mockups.length === 0 && !error && (
                 <p style={{ textAlign: 'center' }}>No mockups found {isSearching && activeSearchTerm ? `for "${activeSearchTerm}"` : 'matching your criteria, or click "".'}</p>
            )}

            {mockups.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                    <div className="pagination-buttons" style={{ marginBottom: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                        <button
                            onClick={handlePreviousPage}
                            disabled={isLoading || currentPage <= 1}
                            style={paginationButtonStyle}
                            onMouseOver={e => Object.assign(e.target.style, paginationButtonHoverStyle)}
                            onMouseOut={e => Object.assign(e.target.style, paginationButtonStyle)}
                        >
                            <span style={{fontSize: '1.3em', marginRight: '6px'}}>&larr;</span> Previous Page
                        </button>
                        <button
                            onClick={handleNextPage}
                            disabled={isLoading || currentPage >= totalPages}
                            style={{ ...paginationButtonStyle, marginLeft: '10px' }}
                            onMouseOver={e => Object.assign(e.target.style, paginationButtonHoverStyle)}
                            onMouseOut={e => Object.assign(e.target.style, paginationButtonStyle)}
                        >
                            Next Page <span style={{fontSize: '1.3em', marginLeft: '6px'}}>&rarr;</span>
                        </button>
                    </div>
                    <div className="mockup-grid" style={mockupGridStyle}>
                        {mockups.map((mockup) => {
                            const isSelected = mockup.nr === selectedMockupId;
                            let itemStyle = baseMockupItemStyle;
                            if (logoFile) {
                                itemStyle = isSelected ? selectedMockupItemStyle : clickableMockupItemStyle;
                            }
                            const generatedUrl = generatedMockups[mockup.nr];
                            return (
                                <div 
                                    key={mockup.nr} 
                                    className="mockup-item"
                                    style={{...itemStyle, position: 'relative'}}
                                    onClick={() => logoFile && onMockupSelect(mockup.nr)}
                                >
                                    {/* Loading overlay spinner */}
                                    {mockupLoading[mockup.nr] && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            background: 'rgba(255,255,255,0.7)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 2,
                                            borderRadius: '8px',
                                        }}>
                                            <div className="mockup-spinner" style={{
                                                width: 48,
                                                height: 48,
                                                border: '6px solid #003366',
                                                borderTop: '6px solid #d7262c',
                                                borderRadius: '50%',
                                                animation: 'mockup-spin 1s linear infinite',
                                            }} />
                                        </div>
                                    )}
                                    <img src={generatedUrl || mockup.preview || mockup.thumbnail} alt={mockup.name} className="mockup-image" style={mockupImageStyle} />
                                    <div style={{ fontSize: '0.9em'}}>
                                        <strong>{mockup.name}</strong><br />
                                       
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* Removed duplicate search bar at the bottom */}
            {/* 
            <div className="controls-container" style={controlsContainerStyle}>
                <input
                    className="search-input"
                    type="text"
                    value={searchQuery}
                    onChange={e => onSearchChange(e.target.value)}
                    style={searchInputStyle}
                    placeholder="Search mockups (e.g., hat, shirt, mug)"
                />
                <button style={buttonStyle} onClick={onSearch}>Search</button>
            </div>
            */}
        </div>
    );
};

export default MockupListPage; 

/* Add spinner animation CSS */
/* Add this at the bottom of the file */
/*
@keyframes mockup-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
*/ 